import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  UserCircleIcon,
  BookOpenIcon,
  PuzzlePieceIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/solid';
import { useAuth } from '@/features/auth/useAuth';
import { useHaptics } from '@/lib/hooks/useHaptics';
import { setNavDirection } from '@/lib/navDirection';
import { Button } from '@/components/ui/Button';
import { fetchMyEnrollments } from '@/features/courses/api';

function useTotalXP(uid: string | null): number {
  const [xp, setXp] = useState(0);
  useEffect(() => {
    if (!uid) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setXp(0);
      return;
    }
    let cancelled = false;
    fetchMyEnrollments()
      .then((es) => {
        if (cancelled) return;
        setXp(es.reduce((s, e) => s + (e.earnedXP ?? 0), 0));
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [uid]);
  return xp;
}

const glassStyle = {
  background: 'rgba(250, 250, 250, 1)',
  backdropFilter: 'blur(32px) saturate(180%)',
  WebkitBackdropFilter: 'blur(32px) saturate(180%)',
  border: '0.5px solid rgba(255,255,255,1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset',
} as const;

function PhotoAvatar({ src, alt, imgClassName, fallback }: { src: string; alt: string; imgClassName: string; fallback: React.ReactNode }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <>{fallback}</>;
  return <img src={src} alt={alt} className={imgClassName} onError={() => setFailed(true)} />;
}

function MobileBottomNav() {
  const { user, isAllowedUser, loading } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const h = useHaptics();
  const navRef = useRef<HTMLElement>(null);
  const [heldIdx, setHeldIdx] = useState<number | null>(null);

  const nav = [
    { path: '/courses', label: 'Courses', icon: BookOpenIcon, exact: false },
    { path: '/join', label: 'Join', icon: PuzzlePieceIcon, exact: true },
    ...(user && isAllowedUser
      ? [{ path: '/dashboard', label: 'Dashboard', icon: Squares2X2Icon, exact: false }]
      : []),
  ];

  const activeIdx = nav.findIndex(({ path, exact }) =>
    exact ? pathname === path : pathname.startsWith(path)
  );
  const clampedIdx: number | null = activeIdx === -1 ? null : activeIdx;
  const tabCount = nav.length;
  const isHeld = heldIdx !== null;
  const displayIdx: number | null = heldIdx ?? clampedIdx;
  const isAccountActive = pathname.startsWith('/account') || pathname.startsWith('/sign-in');

  const getIdxFromX = (clientX: number): number => {
    if (!navRef.current) return 0;
    const rect = navRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    return Math.max(0, Math.min(tabCount - 1, Math.floor((x / rect.width) * tabCount)));
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setHeldIdx(getIdxFromX(e.clientX));
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLElement>) => {
    if (!isHeld) return;
    const idx = getIdxFromX(e.clientX);
    if (idx !== heldIdx) {
      setHeldIdx(idx);
      h.drag();
    }
  };

  const handlePointerUp = (_e: React.PointerEvent<HTMLElement>) => {
    if (heldIdx !== null) {
      const target = nav[heldIdx];
      if (target) {
        if (heldIdx !== clampedIdx) {
          setNavDirection(clampedIdx === null || heldIdx > clampedIdx ? 'right' : 'left');
        }
        navigate(target.path);
      }
      h.tap();
    }
    setHeldIdx(null);
  };

  const CIRCLE_SIZE = 58;

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-50"
      style={{ filter: 'drop-shadow(0 2px 32px rgba(0,0,0,0.06))' }}
    >
      <div className="relative pb-[max(0.5rem,env(safe-area-inset-bottom))]" style={{ height: `calc(${CIRCLE_SIZE}px + max(0.5rem, env(safe-area-inset-bottom)))` }}>
        {/* Centered pill */}
        <nav
          ref={navRef}
          className="absolute bottom-[max(0.5rem,env(safe-area-inset-bottom))] left-[calc(50%-37px)] -translate-x-1/2 flex items-center p-1 rounded-full select-none touch-none"
          style={glassStyle}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={() => setHeldIdx(null)}
        >
          {displayIdx !== null && (
            <div
              className="absolute rounded-full pointer-events-none"
              style={{
                width: `calc((100% - 8px) / ${tabCount})`,
                height: 'calc(100% - 8px)',
                left: `calc(4px + ${displayIdx} * (100% - 8px) / ${tabCount})`,
                top: '50%',
                willChange: 'transform, left',
                transform: isHeld ? 'translateY(-50%) scale(1.1)' : 'translateY(-50%) scale(1)',
                background: 'linear-gradient(160deg, rgba(92,186,132,0.18) 0%, rgba(60,160,103,0.10) 100%)',
                boxShadow: isHeld
                  ? 'inset 0 0px 0 rgba(255,255,255,0.7), 0 2px 8px rgba(60,160,103,0.22)'
                  : 'inset 0 0px 0 rgba(255,255,255,0.6), 0 2px 8px rgba(60,160,103,0.12)',
                transition: [
                  'left 420ms cubic-bezier(0.4, 0, 0.2, 1)',
                  'transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                  'box-shadow 200ms ease',
                ].join(', '),
              }}
            />
          )}
          {nav.map(({ path, label, icon: Icon }, idx) => (
            <div
              key={path}
              className={`relative z-10 w-[80px] flex flex-col items-center justify-center gap-0.5 py-1.5 px-1 text-[10px] font-semibold transition-colors duration-200 will-change-transform ${displayIdx !== null && idx === displayIdx ? 'text-brand-600' : 'text-[#000000]'
                }`}
            >
              <Icon className="w-6 h-6" />
              <span>{label}</span>
            </div>
          ))}
        </nav>

        {/* Account / Sign-in circle — pinned to right edge */}
        <Link
          to={loading ? '#' : user ? '/account' : '/sign-in'}
          onClick={() => { if (!loading) h.tap(); }}
          aria-label={user ? 'Account' : 'Sign in'}
          className="absolute bottom-[max(0.5rem,env(safe-area-inset-bottom))] right-4 flex items-center justify-center rounded-full"
          style={{ ...glassStyle, width: `${CIRCLE_SIZE}px`, height: `${CIRCLE_SIZE}px` }}
        >
          {user?.photoURL ? (
            <PhotoAvatar
              src={user.photoURL}
              alt={user.displayName ?? 'Account'}
              imgClassName="w-10 h-10 rounded-full object-cover shadow-sm"
              fallback={<UserCircleIcon className={`w-7 h-7 ${isAccountActive ? 'text-brand-600' : 'text-[#000000]'}`} />}
            />
          ) : (
            <UserCircleIcon className={`w-7 h-7 ${loading ? 'text-[#cccccc]' : isAccountActive ? 'text-brand-600' : 'text-[#000000]'}`} />
          )}
        </Link>
      </div>
    </div>
  );
}

export function Navbar({ transparent = false }: { transparent?: boolean }) {
  const { user, isAllowedUser, loading } = useAuth();
  const totalXP = useTotalXP(user?.uid ?? null);
  const { pathname } = useLocation();

  const t = transparent;
  const isCoursesActive = pathname.startsWith('/courses');
  const isJoinActive = pathname === '/join';
  const isDashboardActive = pathname.startsWith('/dashboard');

  const linkClass = (active: boolean) =>
    `transition-colors ${t
      ? active ? 'text-white' : 'text-white/70 hover:text-white'
      : active ? 'text-[#1a1a1a]' : 'text-[#888888] hover:text-[#1a1a1a]'
    }`;

  return (
    <>
      <header className={`${t ? 'absolute top-0 left-0 right-0 home-nav' : 'sticky top-0 border-b border-[#eeeeee] bg-white'} z-50 hidden md:block`}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
  <img 
    src={t ? "/faviconwhite.png" : "/favicon.png"} 
    alt="Logo" 
    className="h-6 w-6 object-contain" 
  />
  <span 
    className={`font-serif text-xl font-bold tracking-tight 
      ${t 
        ? 'text-white' 
        : 'bg-gradient-to-br from-[#43a074] via-[#3ba696] to-[#87a9d6] bg-clip-text text-transparent'
      }`}
  >
    Learn
  </span>
</Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/courses" className={linkClass(isCoursesActive)}>
              Courses
            </Link>
            <Link to="/join" className={linkClass(isJoinActive)}>
              Join a Game
            </Link>
            {loading ? (
              <div className="h-8 w-8 rounded-full bg-gray-100 animate-pulse" />
            ) : user && isAllowedUser ? (
              <>
                <Link to="/dashboard" className={linkClass(isDashboardActive)}>
                  Dashboard
                </Link>
                <span
                  title="Your total XP across all courses"
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${t ? 'bg-white/20 text-white' : 'bg-brand-50 text-brand-700'
                    }`}
                >
                  ⚡ {totalXP.toLocaleString()} XP
                </span>
                <Link to="/account" aria-label="Account">
                  {user.photoURL ? (
                    <PhotoAvatar
                      src={user.photoURL}
                      alt={user.displayName ?? 'Account'}
                      imgClassName="h-8 w-8 rounded-full object-cover ring-2 ring-transparent hover:ring-brand-300 transition-all"
                      fallback={<UserCircleIcon className={`h-8 w-8 transition-colors ${t ? 'text-white/70 hover:text-white' : 'text-[#aaaaaa] hover:text-brand-500'}`} />}
                    />
                  ) : (
                    <UserCircleIcon className={`h-8 w-8 transition-colors ${t ? 'text-white/70 hover:text-white' : 'text-[#aaaaaa] hover:text-brand-500'}`} />
                  )}
                </Link>
              </>
            ) : !user ? (
              <Link to="/sign-in">
                <Button className={t ? 'bg-white/20 hover:bg-white/30 text-white border border-white/30' : 'bg-brand-500 hover:bg-brand-600 text-white'}>Sign in</Button>
              </Link>
            ) : null}
          </nav>
        </div>
      </header>

      <MobileBottomNav />
    </>
  );
}
